// src/components/Shimmer.tsx
/**
 * Reusable shimmer skeleton components built on top of MUI Skeleton.
 * Use these anywhere a loading spinner was previously shown for page content.
 *
 * Exports:
 *  - TableShimmer       – for table-based pages (Subjects, Classes, Chapters, Users)
 *  - CardShimmer        – for card-based pages (Doubts)
 *  - DetailShimmer      – for profile/detail pages (UserDetails)
 *  - StatShimmer        – for dashboard stat cards
 *  - TabContentShimmer  – for tab-panel content (ChapterResources)
 */
import React from 'react';
import { Box, Skeleton, Stack, Paper } from '@mui/material';

// ── Table shimmer ──────────────────────────────────────────────────────────────
interface TableShimmerProps {
    rows?: number;
    cols?: number;
}
export function TableShimmer({ rows = 7, cols = 5 }: TableShimmerProps) {
    return (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            {/* Header row */}
            <Box sx={{ display: 'flex', gap: 2, px: 2.5, py: 1.5, background: '#f8fafc', borderBottom: '1px solid', borderColor: 'divider' }}>
                {Array.from({ length: cols }).map((_, i) => (
                    <Skeleton key={i} animation="wave" height={16} width={`${100 / cols}%`} sx={{ borderRadius: 1 }} />
                ))}
            </Box>
            {/* Data rows */}
            {Array.from({ length: rows }).map((_, ri) => (
                <Box key={ri} sx={{
                    display: 'flex', gap: 2, px: 2.5, py: 1.5, alignItems: 'center',
                    borderBottom: ri < rows - 1 ? '1px solid' : 'none', borderColor: 'divider',
                    background: ri % 2 === 0 ? '#fff' : '#fafafa',
                }}>
                    {/* First col: avatar + two lines */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: `${100 / cols}%`, flexShrink: 0 }}>
                        <Skeleton animation="wave" variant="circular" width={36} height={36} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton animation="wave" height={14} width="70%" sx={{ mb: 0.5, borderRadius: 1 }} />
                            <Skeleton animation="wave" height={11} width="50%" sx={{ borderRadius: 1 }} />
                        </Box>
                    </Box>
                    {/* Remaining cols */}
                    {Array.from({ length: cols - 1 }).map((_, ci) => (
                        <Box key={ci} sx={{ flex: 1 }}>
                            <Skeleton animation="wave" height={14} width={`${60 + (ci * 7) % 30}%`} sx={{ borderRadius: 1 }} />
                        </Box>
                    ))}
                </Box>
            ))}
        </Paper>
    );
}

// ── Card shimmer ───────────────────────────────────────────────────────────────
interface CardShimmerProps {
    count?: number;
}
export function CardShimmer({ count = 6 }: CardShimmerProps) {
    return (
        <Stack spacing={1.5}>
            {Array.from({ length: count }).map((_, i) => (
                <Paper key={i} elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Skeleton animation="wave" variant="circular" width={44} height={44} sx={{ flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                <Skeleton animation="wave" height={15} width="35%" sx={{ borderRadius: 1 }} />
                                <Skeleton animation="wave" height={15} width="12%" sx={{ borderRadius: 1 }} />
                            </Box>
                            <Skeleton animation="wave" height={13} width="90%" sx={{ mb: 0.5, borderRadius: 1 }} />
                            <Skeleton animation="wave" height={13} width="75%" sx={{ mb: 1.5, borderRadius: 1 }} />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Skeleton animation="wave" height={22} width={70} sx={{ borderRadius: 5 }} />
                                <Skeleton animation="wave" height={22} width={55} sx={{ borderRadius: 5 }} />
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            ))}
        </Stack>
    );
}

// ── Detail / Profile shimmer ───────────────────────────────────────────────────
export function DetailShimmer() {
    return (
        <Box>
            {/* Hero card */}
            <Paper elevation={0} sx={{ p: 4, mb: 3, borderRadius: 4, background: 'linear-gradient(135deg, #e8f4f5 0%, #d1eff0 100%)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Skeleton animation="wave" variant="circular" width={80} height={80} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton animation="wave" height={28} width="40%" sx={{ mb: 1, borderRadius: 1 }} />
                        <Skeleton animation="wave" height={16} width="55%" sx={{ mb: 1.5, borderRadius: 1 }} />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {[80, 90, 70].map((w, i) => (
                                <Skeleton key={i} animation="wave" height={24} width={w} sx={{ borderRadius: 5 }} />
                            ))}
                        </Box>
                    </Box>
                    <Skeleton animation="wave" height={42} width={130} sx={{ borderRadius: 2 }} />
                </Box>
                {/* Stats strip */}
                <Box sx={{ display: 'flex', mt: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.06)', gap: 2 }}>
                    {[1, 2, 3, 4].map(i => (
                        <Box key={i} sx={{ flex: 1, textAlign: 'center' }}>
                            <Skeleton animation="wave" height={24} width="50%" sx={{ mx: 'auto', mb: 0.5, borderRadius: 1 }} />
                            <Skeleton animation="wave" height={13} width="70%" sx={{ mx: 'auto', borderRadius: 1 }} />
                        </Box>
                    ))}
                </Box>
            </Paper>

            {/* Tab body */}
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                {/* Tab bar */}
                <Box sx={{ display: 'flex', px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', gap: 3 }}>
                    {[90, 130, 110].map((w, i) => (
                        <Skeleton key={i} animation="wave" height={32} width={w} sx={{ borderRadius: 1 }} />
                    ))}
                </Box>
                {/* Content */}
                <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        {[0, 1].map(col => (
                            <Box key={col} sx={{ flex: 1 }}>
                                <Skeleton animation="wave" height={14} width="35%" sx={{ mb: 1.5, borderRadius: 1 }} />
                                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                    {[1, 2, 3, 4].map(row => (
                                        <Box key={row} sx={{ display: 'flex', gap: 2, p: 1.5, alignItems: 'center', borderBottom: row < 4 ? '1px solid' : 'none', borderColor: 'divider' }}>
                                            <Skeleton animation="wave" variant="circular" width={20} height={20} />
                                            <Box sx={{ flex: 1 }}>
                                                <Skeleton animation="wave" height={10} width="30%" sx={{ mb: 0.5, borderRadius: 1 }} />
                                                <Skeleton animation="wave" height={14} width="65%" sx={{ borderRadius: 1 }} />
                                            </Box>
                                        </Box>
                                    ))}
                                </Paper>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}

// ── Stat card shimmer (Dashboard) ──────────────────────────────────────────────
export function StatShimmer({ count = 6 }: { count?: number }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
            {Array.from({ length: count }).map((_, i) => (
                <Paper key={i} elevation={0} sx={{ borderRadius: 3, p: 2.5, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Skeleton animation="wave" variant="rounded" width={44} height={44} sx={{ borderRadius: 2 }} />
                        <Skeleton animation="wave" height={20} width={55} sx={{ borderRadius: 5 }} />
                    </Box>
                    <Skeleton animation="wave" height={32} width="55%" sx={{ mb: 0.5, borderRadius: 1 }} />
                    <Skeleton animation="wave" height={13} width="70%" sx={{ borderRadius: 1 }} />
                </Paper>
            ))}
        </Box>
    );
}

// ── Tab-content shimmer (ChapterResources) ─────────────────────────────────────
export function TabContentShimmer({ rows = 4 }: { rows?: number }) {
    return (
        <Stack spacing={1.5}>
            {Array.from({ length: rows }).map((_, i) => (
                <Paper key={i} elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Skeleton animation="wave" variant="rounded" width={36} height={36} sx={{ borderRadius: 1.5 }} />
                            <Box>
                                <Skeleton animation="wave" height={14} width={160} sx={{ mb: 0.5, borderRadius: 1 }} />
                                <Skeleton animation="wave" height={11} width={100} sx={{ borderRadius: 1 }} />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Skeleton animation="wave" height={32} width={80} sx={{ borderRadius: 1.5 }} />
                            <Skeleton animation="wave" height={32} width={80} sx={{ borderRadius: 1.5 }} />
                        </Box>
                    </Box>
                </Paper>
            ))}
        </Stack>
    );
}
